import React, { useCallback, useState, useMemo } from "react";
import { callService, userService } from "/app/src/services";
import { Helmet } from "react-helmet";
import DateTime from "/app/src/components/generic/formatting/dateTime";
import { getDateFormat } from "/app/src/helpers/time";
import Controls from "./controls";
import { useTranslation } from "react-i18next";
import { User, apiCall } from "/app/src/models";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { IconToolTip } from "/app/src/components/icons/IconBuilder";
import { roundDecimal } from "../../helpers/data";
import { useTimezoneContext } from "/app/src/contexts/hooks/useTimezoneContext";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import { useSearchParams } from "react-router-dom";
import useQueryParams from "/app/src/hooks/useQueryParams";

/**
 * Build the params for the api call. Calculates the creation date range, and
 * builds the params for the search, status, and user.
 */
function buildCallParams(
  searchString: string,
  afterTime: string,
  beforeTime: string,
  selectedStatus: string,
  selectedUser: string,
  endpoint: string,
) {
  const creationDate = [];
  if (afterTime !== "") {
    creationDate.push(`[gt]${getDateFormat(afterTime, "YYYY-MM-DDTHH:mm:ss")}`);
  }
  if (beforeTime !== "") {
    creationDate.push(
      `[lt]${getDateFormat(beforeTime, "YYYY-MM-DDTHH:mm:ss")}`,
    );
  }

  return {
    search: searchString,
    creationDate,
    code: selectedStatus,
    userId: selectedUser,
    endpoint,
  };
}

/**
 * Component for displaying the api calls
 */
export default function Calls() {
  const { t } = useTranslation();
  const [sort, setSort] = useSortUpgrade([]);
  const [searchParams] = useSearchParams();

  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [beforeTime, setBeforeTime] = useState("");
  const [afterTime, setAfterTime] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedUser, setSelectedUser] = useState("all");
  const [isTextLimitEnabled, setIsTextLimitEnabled] = useState(false);
  const queryClient = useQueryClient();
  const { timeZone } = useTimezoneContext();
  const endpoint = "[notlike]refresh";
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  //limit the text to 100 characters
  const limitText = (value: string) => {
    if (value) {
      return value.length > 100 ? `${value.substring(0, 100)} ...` : value;
    }
    return null;
  };

  //handler for toggling on and off the text limit
  const toggleTextLimit = useCallback(() => {
    setIsTextLimitEnabled((prev) => !prev);
  }, []);

  const columnHelper = createColumnHelper<apiCall>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("creationDate", {
        id: "creationDate",
        cell: (info) => {
          if (info.getValue() !== null) {
            return (
              <DateTime
                date={info.getValue()}
                format={"MMMM Do YYYY, h:mm:ss.SS a"}
              />
            );
          } else {
            return <> </>;
          }
        },
        header: () => (
          <div className="flex items-center">
            {t("translation:date/time")}
            <IconToolTip
              content={`${t("translation:times_displayed")} ${timeZone} ${t("translation:time_zone")}`}
              className="ml-1"
            />
          </div>
        ),
      }),
      columnHelper.accessor("code", {
        id: "code",
        cell: (info) => info.getValue(),
        header: () => t("translation:code"),
      }),
      columnHelper.accessor("duration", {
        id: "duration",
        cell: (info) => {
          if (info.getValue() !== null) {
            return <>{roundDecimal(info.getValue() as string, 1)} ms</>;
          } else {
            return <> </>;
          }
        },
        header: () => t("translation:duration"),
      }),
      columnHelper.accessor("endpoint", {
        id: "endpoint",
        cell: (info) => info.getValue(),
        header: () => t("translation:endpoint"),
      }),
      columnHelper.accessor("username", {
        id: "username",
        cell: (info) => info.getValue(),
        header: () => t("translation:user"),
      }),
      columnHelper.accessor("body", {
        id: "body",
        cell: (info) => {
          if (isTextLimitEnabled) {
            return <>{limitText(info.getValue())}</>;
          } else {
            return <>{info.getValue()}</>;
          }
        },
        header: () => t("translation:body"),
      }),
      columnHelper.accessor("response", {
        id: "response",
        cell: (info) => {
          if (isTextLimitEnabled) {
            return <>{limitText(info.getValue())}</>;
          } else {
            return <>{info.getValue()}</>;
          }
        },
        header: () => t("translation:response"),
      }),
      columnHelper.accessor("error", {
        id: "error",
        cell: (info) => {
          if (isTextLimitEnabled) {
            return <>{limitText(info.getValue())}</>;
          } else {
            return <>{info.getValue()}</>;
          }
        },
        header: () => t("translation:error"),
      }),
    ],
    [columnHelper, isTextLimitEnabled, t, timeZone],
  );

  //Get the list of all users
  const { data: users } = useQuery({
    queryKey: ["users"],
    queryFn: () => {
      return userService.getAll();
    },
    initialData: { users: [] },
    select: (data: { users: User[] }) => {
      return data.users;
    },
  });

  const {
    data: apiCalls,
    count: apiCallsCount,
    isFetching,
    getQueryKeys,
  } = usePaginatedData<apiCall>({
    queryKey: ["calls"],
    sort,
    page,
    pageSize,
    searchString,
    service: callService,
    options: buildCallParams(
      searchString,
      afterTime,
      beforeTime,
      selectedStatus,
      selectedUser,
      endpoint,
    ),
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: apiCallsCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  //handler for clearing logs button
  const clearLogs = useCallback(() => {
    const { dataQueryKey, countQueryKey } = getQueryKeys();
    callService.deleteAll().then(() => {
      queryClient.setQueryData(dataQueryKey, { calls: [] });
      queryClient.setQueryData(countQueryKey, { count: 0 });
    });
  }, [getQueryKeys, queryClient]);

  return (
    <div className="calls">
      <Helmet>
        <title>{t("translation:api_calls")} - ItemPath</title>
      </Helmet>
      <Controls
        clearLogs={clearLogs}
        setSelectedStatus={setSelectedStatus}
        setSelectedUser={setSelectedUser}
        setAfterTime={setAfterTime}
        setBeforeTime={setBeforeTime}
        users={users}
        setSearchString={setSearchString}
        isTextLimitEnabled={isTextLimitEnabled}
        toggleTextLimit={toggleTextLimit}
      />

      <Table
        loading={isFetching}
        rows={apiCalls}
        tableColumns={columns}
        length={apiCallsCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        emptyText={"No Calls Found"}
      />
    </div>
  );
}
